// src/features/affiliate/OfferFormDialog.tsx
// Admin: create or edit an offer — payout terms, caps/budgets, and targeting
// (countries + traffic types, both free-text — see ChipInput, kept as-is:
// the platform is country-agnostic so these stay data-driven chip lists,
// never a hardcoded country dropdown).

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useAffiliateOffer, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import type { Currency, Offer, OfferPayoutType } from '@core/types/affiliate';
import { CurrencyValues } from '@core/types/affiliate';
import { ChipInput } from './ChipInput';
import { errMessage, makeEntity } from './format';

const PAYOUT_TYPES: OfferPayoutType[] = [
  'OFFER_PAYOUT_TYPE_CPA',
  'OFFER_PAYOUT_TYPE_CPS',
  'OFFER_PAYOUT_TYPE_CPL',
  'OFFER_PAYOUT_TYPE_CPI',
  'OFFER_PAYOUT_TYPE_REVSHARE',
  'OFFER_PAYOUT_TYPE_HYBRID',
];

const isPercentType = (t?: OfferPayoutType) => t === 'OFFER_PAYOUT_TYPE_CPS' || t === 'OFFER_PAYOUT_TYPE_REVSHARE';

function slugify(v: string): string {
  return v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

interface OfferFormDialogProps {
  open: boolean;
  onClose: () => void;
  offer: Offer | null;
  onSaved: (offer: Offer) => void;
}

export const OfferFormDialog = observer(({ open, onClose, offer, onSaved }: OfferFormDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useAffiliateOffer();
  const isEdit = !!offer;
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [payoutType, setPayoutType] = useState<OfferPayoutType>('OFFER_PAYOUT_TYPE_CPA');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutPercent, setPayoutPercent] = useState('');
  const [currency, setCurrency] = useState<Currency>('CURRENCY_USD');
  const [holdPeriodDays, setHoldPeriodDays] = useState('0');
  const [conversionGoal, setConversionGoal] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [dailyCap, setDailyCap] = useState('0');
  const [totalCap, setTotalCap] = useState('0');
  const [allowedCountries, setAllowedCountries] = useState<string[]>([]);
  const [blockedCountries, setBlockedCountries] = useState<string[]>([]);
  const [allowedTraffic, setAllowedTraffic] = useState<string[]>([]);
  const [blockedTraffic, setBlockedTraffic] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(offer?.name ?? '');
    setDescription(offer?.description ?? '');
    setSlug(offer?.slug ?? '');
    setSlugTouched(isEdit);
    setPayoutType(offer?.payout_type ?? 'OFFER_PAYOUT_TYPE_CPA');
    setPayoutAmount(offer?.payout_amount ? String(offer.payout_amount.amount / Math.pow(10, offer.payout_amount.decimal_places || 2)) : '');
    setPayoutPercent(offer?.payout_percent ?? '');
    setCurrency(offer?.currency ?? 'CURRENCY_USD');
    setHoldPeriodDays(String(offer?.hold_period_days ?? 0));
    setConversionGoal(offer?.conversion_goal ?? '');
    setIsPublic(offer?.is_public ?? true);
    setRequiresApproval(offer?.requires_approval ?? false);
    setDailyCap(String(offer?.daily_conversion_cap ?? 0));
    setTotalCap(String(offer?.total_conversion_cap ?? 0));
    setAllowedCountries(offer?.allowed_countries ?? []);
    setBlockedCountries(offer?.blocked_countries ?? []);
    setAllowedTraffic(offer?.allowed_traffic_types ?? []);
    setBlockedTraffic(offer?.blocked_traffic_types ?? []);
    setPreviewUrl(offer?.preview_url ?? '');
    setMoreFields(false);
  }, [open, offer, isEdit]);

  const dirty = !!(name.trim() || description.trim() || slug.trim());

  const save = async () => {
    if (!name.trim() || !slug.trim()) return;
    setIsSaving(true);
    setError(null);
    const amountMinor = payoutAmount ? Math.round(Number(payoutAmount) * 100) : 0;
    try {
      if (isEdit && offer?.entity) {
        const saved = await store.UpdateOffer(offer.entity.id, {
          entity: offer.entity,
          update_mask: [
            'name', 'description', 'payout_type', 'payout_amount', 'payout_percent', 'hold_period_days',
            'conversion_goal', 'is_public', 'requires_approval', 'daily_conversion_cap', 'total_conversion_cap',
            'allowed_countries', 'blocked_countries', 'allowed_traffic_types', 'blocked_traffic_types', 'preview_url',
          ],
          name: name.trim(),
          description,
          payout_type: payoutType,
          payout_amount: { amount: amountMinor, currency, decimal_places: 2 },
          payout_percent: payoutPercent,
          hold_period_days: Number(holdPeriodDays) || 0,
          conversion_goal: conversionGoal,
          is_public: isPublic,
          requires_approval: requiresApproval,
          daily_conversion_cap: Number(dailyCap) || 0,
          total_conversion_cap: Number(totalCap) || 0,
          daily_budget: undefined,
          total_budget: undefined,
          allowed_countries: allowedCountries,
          blocked_countries: blockedCountries,
          allowed_traffic_types: allowedTraffic,
          blocked_traffic_types: blockedTraffic,
          preview_url: previewUrl,
          starts_at: offer.starts_at,
          ends_at: offer.ends_at,
          metadata: offer.metadata ?? {},
        });
        onSaved(saved);
      } else {
        const saved = await store.CreateOffer({
          entity: makeEntity('', 'offer', scope),
          name: name.trim(),
          description,
          slug: slug.trim(),
          payout_type: payoutType,
          payout_amount: { amount: amountMinor, currency, decimal_places: 2 },
          payout_percent: payoutPercent,
          currency,
          hold_period_days: Number(holdPeriodDays) || 0,
          conversion_goal: conversionGoal,
          is_public: isPublic,
          requires_approval: requiresApproval,
          daily_conversion_cap: Number(dailyCap) || 0,
          total_conversion_cap: Number(totalCap) || 0,
          allowed_countries: allowedCountries,
          blocked_countries: blockedCountries,
          allowed_traffic_types: allowedTraffic,
          blocked_traffic_types: blockedTraffic,
          preview_url: previewUrl,
          metadata: {},
        });
        onSaved(saved);
      }
      onClose();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.offers.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={save}
      submitLabel={isEdit ? t('common.saveChanges') : t('common.create')}
      canSubmit={!!name.trim() && !!slug.trim()}
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('affiliate.admin.offers.editTitle') : t('affiliate.admin.offers.createTitle')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <TitleInput
          value={name}
          onChange={(v) => {
            setName(v);
            if (!slugTouched && !isEdit) setSlug(slugify(v));
          }}
          placeholder={`${t('common.name')}…`}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('common.name')}
        />
        <AutoGrowTextarea ref={descRef} value={description} onChange={setDescription} placeholder={`${t('common.description')}…`} minRows={1} />
      </div>

      <div className="px-3.5 pb-2">
        <FormRow label={t('affiliate.admin.offers.slug')} labelWidth="sm">
          <input
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            disabled={isEdit}
            aria-label={t('affiliate.admin.offers.slug')}
            className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </FormRow>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <div className="w-40">
          <Select value={payoutType} onChange={(e) => setPayoutType(e.target.value as OfferPayoutType)} options={PAYOUT_TYPES.map((v) => ({ value: v, label: t(`affiliate.payoutType.${v.replace('OFFER_PAYOUT_TYPE_', '').toLowerCase()}`) }))} aria-label={t('affiliate.offers.payoutTerms')} fullWidth />
        </div>
        {isPercentType(payoutType) ? (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
            <input type="text" inputMode="decimal" value={payoutPercent} onChange={(e) => setPayoutPercent(e.target.value)} placeholder="30.00" aria-label={t('affiliate.admin.offers.payoutPercent')} className="w-16 bg-transparent text-right text-sm tabular-nums text-foreground outline-none" />
            <span className="text-xs text-muted-foreground">%</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
            <input type="text" inputMode="decimal" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder="0.00" aria-label={t('affiliate.admin.offers.payoutAmount')} className="w-20 bg-transparent text-right text-sm tabular-nums text-foreground outline-none" />
          </span>
        )}
        <div className="w-28">
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} options={CurrencyValues.filter((v) => v !== 'CURRENCY_UNSPECIFIED').map((v) => ({ value: v, label: v.replace('CURRENCY_', '') }))} aria-label={t('affiliate.field.currency')} fullWidth />
        </div>
      </div>

      {moreFields && (
        <div className="space-y-2 border-t border-border px-3.5 py-2">
          <div className="flex flex-wrap gap-2">
            {[
              { label: t('affiliate.admin.offers.holdPeriodDays'), value: holdPeriodDays, set: setHoldPeriodDays },
              { label: t('affiliate.admin.offers.dailyCap'), value: dailyCap, set: setDailyCap },
              { label: t('affiliate.admin.offers.totalCap'), value: totalCap, set: setTotalCap },
            ].map((f) => (
              <span key={f.label} className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
                <span className="text-xs text-muted-foreground">{f.label}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={f.value}
                  onChange={(e) => f.set(e.target.value.replace(/[^0-9]/g, ''))}
                  aria-label={f.label}
                  className="w-14 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
                />
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t('affiliate.admin.offers.zeroUnlimited')}</p>

          <FormRow label={t('affiliate.admin.offers.conversionGoal')} labelWidth="sm">
            <input value={conversionGoal} onChange={(e) => setConversionGoal(e.target.value)} placeholder="signup, purchase, subscription_activated" className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
          </FormRow>
          <FormRow label={t('affiliate.offers.previewLink')} labelWidth="sm">
            <input value={previewUrl} onChange={(e) => setPreviewUrl(e.target.value)} placeholder="https://…" className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
          </FormRow>

          <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
            <FormRow label={t('affiliate.admin.offers.allowedCountries')} labelWidth="sm">
              <ChipInput value={allowedCountries} onChange={setAllowedCountries} placeholder={t('affiliate.admin.offers.countriesPlaceholder')} aria-label={t('affiliate.admin.offers.allowedCountries')} uppercase />
            </FormRow>
            <FormRow label={t('affiliate.admin.offers.blockedCountries')} labelWidth="sm">
              <ChipInput value={blockedCountries} onChange={setBlockedCountries} placeholder={t('affiliate.admin.offers.countriesPlaceholder')} aria-label={t('affiliate.admin.offers.blockedCountries')} uppercase />
            </FormRow>
            <FormRow label={t('affiliate.admin.offers.allowedTraffic')} labelWidth="sm">
              <ChipInput value={allowedTraffic} onChange={setAllowedTraffic} placeholder={t('affiliate.admin.offers.trafficPlaceholder')} aria-label={t('affiliate.admin.offers.allowedTraffic')} />
            </FormRow>
            <FormRow label={t('affiliate.admin.offers.blockedTraffic')} labelWidth="sm">
              <ChipInput value={blockedTraffic} onChange={setBlockedTraffic} placeholder={t('affiliate.admin.offers.trafficPlaceholder')} aria-label={t('affiliate.admin.offers.blockedTraffic')} />
            </FormRow>
          </div>

          <div className="flex flex-wrap gap-4 pt-1">
            <ToggleSwitch label={t('affiliate.admin.offers.isPublic')} checked={isPublic} onChange={setIsPublic} className="w-auto" />
            <ToggleSwitch label={t('affiliate.offers.requiresApproval')} checked={requiresApproval} onChange={setRequiresApproval} className="w-auto" />
          </div>
        </div>
      )}
    </FormDialog>
  );
});
